import { ActivityStatus, AttentionType, daysBetween } from '@kainos/shared';
import type { AttentionItem, Notification } from '@kainos/validation';
import type {
  ActivityDetailView,
  ActivityView,
  DashboardView,
  ExecutiveReportView,
  MilestoneView,
  ProjectDetailView,
  ProjectSummary,
  TeamMemberView,
  TimelineEntry,
} from '@/types/view-models';
import { DEMO_ACTIVITIES, DEMO_ACTIVITY_INSIGHT } from './fixtures/activities';
import { DEMO_ASSISTANT_FALLBACK, DEMO_ASSISTANT_QA } from './fixtures/assistant';
import { DEMO_ATTENTION } from './fixtures/attention';
import { DEMO_EVIDENCE } from './fixtures/evidence';
import { DEMO_NOTIFICATIONS } from './fixtures/notifications';
import {
  DEMO_MILESTONES,
  DEMO_PHASES,
  DEMO_PROJECT_PULSE,
  DEMO_PROJECTS,
} from './fixtures/projects';
import { DEMO_FEED, DEMO_UPDATES } from './fixtures/updates';
import { DEMO_USERS, userName } from './fixtures/users';

/**
 * In-memory implementation of the KainOS API.
 *
 * It returns exactly the read models the real API will return, so switching
 * `VITE_USE_MOCKS` off changes only where the data comes from — never how a
 * screen is written.
 */

/** Simulated latency, so loading states are exercised during development. */
const delay = <T>(value: T, ms = 220): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

type DemoProject = (typeof DEMO_PROJECTS)[number];
type DemoActivity = (typeof DEMO_ACTIVITIES)[number];
type DemoMilestone = (typeof DEMO_MILESTONES)[number];

const projectById = (id: string) => DEMO_PROJECTS.find((project) => project.id === id);
const phaseName = (id?: string) => DEMO_PHASES.find((phase) => phase.id === id)?.name;

function toSummary(project: DemoProject): ProjectSummary {
  const next = DEMO_MILESTONES.filter((milestone) => milestone.projectId === project.id).sort(
    (a, b) => a.dueDate.localeCompare(b.dueDate),
  )[0];

  return {
    ...project,
    managerName: userName(project.managerId),
    ...(next ? { nextMilestone: { name: next.name, dueDate: next.dueDate } } : {}),
  };
}

function toActivityView(activity: DemoActivity): ActivityView {
  return {
    ...activity,
    ownerName: userName(activity.ownerId),
    projectName: projectById(activity.projectId)?.name ?? '',
    phaseName: phaseName(activity.phaseId),
    aiInsight: DEMO_ACTIVITY_INSIGHT[activity.id],
  };
}

function toMilestoneView(milestone: DemoMilestone): MilestoneView {
  return { ...milestone, projectName: projectById(milestone.projectId)?.name ?? '' };
}

/** Colours the timeline dot from the tone of the report, as in the design. */
function toneForUpdate(text: string, isSystem: boolean): TimelineEntry['tone'] {
  if (isSystem) return 'neutral';
  if (/bloque|no es posible|pendiente/i.test(text)) return 'danger';
  if (/finaliz|aprobad|complet|sin observaciones/i.test(text)) return 'success';
  return 'info';
}

function timelineFor(activityId: string): TimelineEntry[] {
  return DEMO_UPDATES.filter((update) => update.activityId === activityId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map((update) => ({
      id: update.id,
      authorName: userName(update.createdBy),
      at: update.createdAt,
      text: update.rawText ?? '',
      tone: toneForUpdate(update.rawText ?? '', update.source === 'SYSTEM'),
    }));
}

const DASHBOARD_PULSE =
  'El proyecto Implementación Planta Norte mantiene un avance saludable. Sin embargo, ' +
  'Montaje Eléctrico Subestación Sur tiene dos actividades bloqueadas por disponibilidad de ' +
  'materiales. Si no se resuelven durante las próximas 48 horas, el hito Puesta en marcha ' +
  'podría desplazarse aproximadamente 3 días.';

function buildKpis(project: DemoProject, activities: DemoActivity[]): ProjectDetailView['kpis'] {
  const open = activities.filter(
    (activity) =>
      activity.status !== ActivityStatus.DONE && activity.status !== ActivityStatus.CANCELLED,
  );
  const now = new Date();

  return [
    { label: 'Progreso', value: `${project.progress}%`, tone: 'info' },
    {
      label: 'Completadas',
      value: activities.filter((activity) => activity.status === ActivityStatus.DONE).length,
      tone: 'success',
    },
    {
      label: 'Atrasadas',
      value: open.filter((activity) => daysBetween(activity.dueDate, now) > 0).length,
      tone: 'warning',
    },
    {
      label: 'Bloqueos activos',
      value: open.filter((activity) => activity.status === ActivityStatus.BLOCKED).length,
      tone: 'danger',
    },
    {
      label: 'Riesgos abiertos',
      value: DEMO_ATTENTION.filter(
        (item) => item.projectId === project.id && item.type === AttentionType.RISK,
      ).length,
      tone: 'warning',
    },
  ];
}

export const mockApi = {
  getDashboard: (greetingName: string): Promise<DashboardView> =>
    delay({
      greetingName,
      attentionCount: DEMO_ATTENTION.length,
      pulse: DASHBOARD_PULSE,
      topAttention: DEMO_ATTENTION.slice(0, 3),
      projects: DEMO_PROJECTS.map(toSummary),
      upcomingMilestones: DEMO_MILESTONES.map(toMilestoneView),
      feed: DEMO_FEED,
    }),

  listProjects: (): Promise<ProjectSummary[]> => delay(DEMO_PROJECTS.map(toSummary)),

  getProject: (projectId: string): Promise<ProjectDetailView> => {
    const project = projectById(projectId);
    if (!project) return Promise.reject(new Error('Proyecto no encontrado'));

    const activities = DEMO_ACTIVITIES.filter((activity) => activity.projectId === projectId);

    return delay({
      project: toSummary(project),
      pulse: DEMO_PROJECT_PULSE[projectId] ?? '',
      kpis: buildKpis(project, activities),
      phases: DEMO_PHASES.filter((phase) => phase.projectId === projectId).map((phase) => ({
        id: phase.id,
        name: phase.name,
        progress: phase.progress,
        tone: phase.progress === 100 ? 'success' : phase.progress > 0 ? 'info' : 'neutral',
      })),
      attention: DEMO_ATTENTION.filter((item) => item.projectId === projectId),
      recentUpdates: DEMO_FEED.slice(0, 3),
      milestones: DEMO_MILESTONES.filter((milestone) => milestone.projectId === projectId).map(
        toMilestoneView,
      ),
      team: DEMO_USERS.filter((user) => user.id !== 'usr_renata').map((user): TeamMemberView => ({
        id: user.id,
        fullName: user.fullName,
        jobTitle: user.id === project.managerId ? 'Project Manager' : user.jobTitle,
        status: user.status,
      })),
      evidence: DEMO_EVIDENCE.filter((item) => item.projectId === projectId),
    });
  },

  listActivities: (filters: { projectId?: string } = {}): Promise<ActivityView[]> =>
    delay(
      DEMO_ACTIVITIES.filter(
        (activity) => !filters.projectId || activity.projectId === filters.projectId,
      ).map(toActivityView),
    ),

  getActivity: (activityId: string): Promise<ActivityDetailView> => {
    const activity = DEMO_ACTIVITIES.find((item) => item.id === activityId);
    if (!activity) return Promise.reject(new Error('Actividad no encontrada'));

    return delay({
      activity: toActivityView(activity),
      timeline: timelineFor(activityId),
      dependencyTitles: activity.dependsOn
        .map((id) => DEMO_ACTIVITIES.find((item) => item.id === id)?.title)
        .filter((title): title is string => Boolean(title)),
    });
  },

  listAttention: (type?: AttentionType): Promise<AttentionItem[]> =>
    delay(type ? DEMO_ATTENTION.filter((item) => item.type === type) : DEMO_ATTENTION),

  listNotifications: (): Promise<Notification[]> => delay(DEMO_NOTIFICATIONS),

  listTeam: (): Promise<TeamMemberView[]> =>
    delay(
      DEMO_USERS.map((user) => ({
        id: user.id,
        fullName: user.fullName,
        jobTitle: user.jobTitle,
        status: user.status,
      })),
    ),

  getExecutiveReport: (projectId: string): Promise<ExecutiveReportView> => {
    const project = projectById(projectId);
    if (!project) return Promise.reject(new Error('Proyecto no encontrado'));

    return delay({
      projectId,
      projectName: project.name,
      periodLabel: 'Últimas 2 semanas',
      summary: DEMO_PROJECT_PULSE[projectId] ?? '',
      progress: project.progress,
      expectedProgress: project.expectedProgress,
      projectedSlipDays: 3,
      achievements: [
        'Levantamiento de terreno bodega finalizado',
        'Avance de montaje piping línea 03 al 75%',
      ],
      decisionsRequired: [
        'Aprobar cambio de proveedor de sensores para desbloquear instalación de tablero eléctrico.',
      ],
    });
  },

  askAssistant: (question: string): Promise<string> => {
    const match = DEMO_ASSISTANT_QA.find((entry) => entry.question === question);
    return delay(match?.answer ?? DEMO_ASSISTANT_FALLBACK, 600);
  },
};
